/** Existing RFQ create attachment limits (multer: 10MB, max 10 files). */
export const RFQ_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const RFQ_ATTACHMENT_MAX_COUNT = 10;

export function attachmentFileKey(file: File): string {
  return `${file.name}::${file.size}::${file.lastModified}`;
}

export function isSameAttachmentFile(a: File, b: File): boolean {
  return (
    a.name === b.name &&
    a.size === b.size &&
    a.lastModified === b.lastModified
  );
}

export function findDuplicateAttachment(
  attachments: File[],
  candidate: File
): File | undefined {
  return attachments.find((f) => isSameAttachmentFile(f, candidate));
}

export type ImportSourceAttachResult =
  | { ok: true; attachments: File[]; added: boolean; reason?: undefined }
  | { ok: false; attachments: File[]; added: false; reason: string };

/**
 * Merge an applied import source File into the existing attachments list.
 * Does not upload; client-side only. Skips duplicates. Enforces existing size/count limits.
 */
export function mergeImportSourceAttachment(
  attachments: File[],
  source: File | null | undefined
): ImportSourceAttachResult {
  if (!source) {
    return { ok: true, attachments: [...attachments], added: false };
  }

  if (findDuplicateAttachment(attachments, source)) {
    return { ok: true, attachments: [...attachments], added: false };
  }

  if (source.size > RFQ_ATTACHMENT_MAX_BYTES) {
    return {
      ok: false,
      attachments: [...attachments],
      added: false,
      reason: `Import source "${source.name}" exceeds the 10MB attachment limit and was not attached.`,
    };
  }

  if (attachments.length >= RFQ_ATTACHMENT_MAX_COUNT) {
    return {
      ok: false,
      attachments: [...attachments],
      added: false,
      reason: `Maximum ${RFQ_ATTACHMENT_MAX_COUNT} attachments reached — import source was not attached.`,
    };
  }

  return {
    ok: true,
    attachments: [...attachments, source],
    added: true,
  };
}

/** Append files to FormData using the existing RFQ create field name. */
export function appendRfqAttachmentsToFormData(
  formData: FormData,
  attachments: File[]
): void {
  for (const file of attachments) {
    formData.append('attachments', file);
  }
}
